<script>
// @ts-nocheck
    import {store as basketStore, updateInsurance} from '$lib/basketStore.svelte.js';
    import {t} from '$utils/lang.js';
    import {Checkbox} from 'm7-library';
    import {untrack} from 'svelte';
    import {insuranceState, MAX_INSURANCE_BIKE_PRICE} from '$lib/insurance.js';
    import {currencyFormatter} from '$utils/formatter.js';

    const ins = $derived(basketStore.insurance);
    const state = $derived(insuranceState(ins));
    // Section visible whenever a bike is in the basket; over the 10.000 € limit
    // it stays visible but disabled with an explanatory message (legacy parity).
    const show = $derived(state.show);
    const overLimit = $derived(state.overLimit);
    // reference: [1]
    const blockedByLeasing = $derived(state.blockedByLeasing);
    const leasingMessage = t('Mit Fahrradleasing ist die 30-Tage-Versicherung nicht kombinierbar. Wähle eine andere Zahlungsart, wenn du sie behalten möchtest.');
    const limitMessage = $derived(
        t('Die Versicherung kann nur für Räder mit einem Gesamtwert bis maximal {max} abgeschlossen werden. Der aktuelle Gesamtwert beträgt {total}.', {
            max: currencyFormatter.format(MAX_INSURANCE_BIKE_PRICE),
            total: currencyFormatter.format(state.total),
        })
    );

    let active = $state(false);
    let birthdate = $state('');
    let consent = $state(false);
    let terms = $state(false);

    // Errors NIE während Eingabe zeigen — nur wenn der User auf Continue /
    // Express-Button klickt UND Validation fehlschlägt (Legacy-Pattern).
    let showErrors = $state(false);

    // Hydrate state from store EINMAL — sobald `ins` initial vorhanden ist.
    // KEINE Re-Hydration auf nachfolgende Store-Updates, sonst entsteht ein
    // Feedback-Loop:
    //   Type → birthdate-Change → auto-save → store.insurance-Update →
    //   ins-Change → hydration → birthdate=ins.birthdate (oft ''/0000-00-00,
    //   wenn Server das Partial-Datum noch nicht persistiert hat) → wieder
    //   auto-save → loop (hunderte GraphQL-Calls).
    let hydrated = $state(false);
    $effect(() => {
        if (!ins || hydrated) return;
        untrack(() => {
            active = !!ins.active;
            birthdate = ins.birthdate && ins.birthdate !== '0000-00-00' ? ins.birthdate : '';
            consent = !!ins.consent;
            terms = !!ins.terms;
            hydrated = true;
        });
    });

    function validateBirthdate(val) {
        if (!val) return t('Bitte gib dein Geburtsdatum ein.');
        const d = new Date(val);
        const now = new Date();
        const age = (now - d) / (365.25 * 24 * 60 * 60 * 1000);
        if (age < 18) return t('Du musst mindestens 18 Jahre alt sein.');
        if (age > 80) return t('Das Alter darf 80 Jahre nicht überschreiten.');
        return '';
    }

    const birthdateErr = $derived(active ? validateBirthdate(birthdate) : '');
    const consentErr = $derived(active && !consent ? t('Bitte stimme der Datenweitergabe zu.') : '');
    const termsErr = $derived(active && !terms ? t('Bitte akzeptiere die Versicherungsbedingungen.') : '');
    const isFormValid = $derived(!active || (!birthdateErr && !consentErr && !termsErr));

    // Auto-save (debounced) bei jeder Änderung — Server kennt User-Intent immer.
    // Vor dem fetch nochmal gegen den Store diffen, damit identische Payloads
    // (z.B. nach Hydration oder externem Store-Refresh ohne lokale Änderung)
    // keine GraphQL-Calls auslösen.
    let saveTimer;
    $effect(() => {
        if (!hydrated) return;
        const payload = {
            valid: isFormValid && active,
            active,
            consent,
            terms,
            birthdate: birthdate || null,
        };
        clearTimeout(saveTimer);
        saveTimer = setTimeout(() => {
            // Date-Validierung NACHGELAGERT: <input type="date"> liefert
            // beim Tippen oft Zwischenwerte (z.B. Jahr "0001" während User
            // 1992 tippt). Die geben einen birthdateErr, sollen aber keinen
            // Save triggern.
            if (active && payload.birthdate && validateBirthdate(payload.birthdate)) {
                return;
            }
            const cur = basketStore.insurance ?? {};
            const curBirth = (cur.birthdate && cur.birthdate !== '0000-00-00') ? cur.birthdate : null;
            if (
                !!cur.active === payload.active
                && !!cur.consent === payload.consent
                && !!cur.terms === payload.terms
                && curBirth === payload.birthdate
                && !!cur.valid === payload.valid
            ) {
                return;
            }
            updateInsurance(payload);
        }, 400);
    });

    // Reset showErrors sobald Form wieder valid wird.
    $effect(() => {
        if (isFormValid) showErrors = false;
    });

    // Über dem Preislimit ist keine Versicherung möglich — niemals aktiv lassen
    // (sonst triggert ein stale active=true einen Auto-Save mit Versicherung).
    $effect(() => {
        if (overLimit && active) active = false;
    });

    // reference: [1] — dieselbe Mechanik bei Leasing. Der Server hat den Wunsch
    // schon zurückgenommen; ohne das hier bliebe die Checkbox lokal angehakt und
    // der Auto-Save schickte ihn sofort wieder los.
    $effect(() => {
        if (blockedByLeasing && active) active = false;
    });

    // Globale Validate-Fn für Continue/Express-Button-Handler. Returns boolean;
    // setzt showErrors=true falls invalid, sodass Fehler zum ersten Mal sichtbar
    // werden.
    $effect(() => {
        window.__m7_validate_insurance = () => {
            if (!show || overLimit || blockedByLeasing) return true;
            if (isFormValid) return true;
            showErrors = true;
            return false;
        };
        return () => {
            if (window.__m7_validate_insurance) delete window.__m7_validate_insurance;
        };
    });
</script>

{#if show}
<div class="p-4 bg-white rounded-lg border border-grey shadow-sm flex justify-between items-start gap-4" data-checkout-section="insurance" data-insurance-over-limit={overLimit} data-insurance-blocked-by-leasing={blockedByLeasing}>
<div class="flex flex-col gap-3 flex-1 min-w-0">
    {#if blockedByLeasing}
        <!-- reference: [1] Leasing schliesst die Versicherung aus. Gleiche Form wie
             das Preislimit: Abschnitt bleibt sichtbar, Checkbox gesperrt, Begruendung
             darunter — sonst verschwindet der Block wortlos, wenn der Kunde im
             Payment-Step Leasing waehlt. -->
        <label class="text-cyprus font-bold text-sm relative w-full flex items-center gap-2 opacity-50 cursor-not-allowed">
            <input type="checkbox" disabled checked={false} class="cursor-not-allowed" data-checkout-action="insurance-toggle" />
            <span>{t('Ja, ich möchte die kostenlose 30-Tage-Versicherung hinzufügen')}</span>
        </label>
        <span class="text-xs text-scarlet" data-form-error="insurance-leasing">{leasingMessage}</span>
    {:else if overLimit}
        <!-- Über 10.000 € Gesamtwert: Versicherung nicht abschließbar. Checkbox
             disabled + rote Begründung (Legacy-Parität). -->
        <label class="text-cyprus font-bold text-sm relative w-full flex items-center gap-2 opacity-50 cursor-not-allowed">
            <input type="checkbox" disabled checked={false} class="cursor-not-allowed" data-checkout-action="insurance-toggle" />
            <span>{t('Ja, ich möchte die kostenlose 30-Tage-Versicherung hinzufügen')}</span>
        </label>
        <span class="text-xs text-scarlet" data-form-error="insurance-over-limit">{limitMessage}</span>
    {:else}
    <Checkbox
        label={t('Ja, ich möchte die kostenlose 30-Tage-Versicherung hinzufügen')}
        name="insurance-toggle"
        bind:value={active}
        class="text-cyprus font-bold"
        data-checkout-action="insurance-toggle"
    />
    <div class="text-xs text-gray-103 flex flex-col gap-0.5 pl-6">
        <span>{t('Schutz bei Diebstahl, Vandalismus & Unfallschäden. Endet automatisch.')}</span>
        <span>{t('*nicht möglich bei Fahrradleasing')}</span>
    </div>

    {#if active}
    <div class="flex flex-col gap-3" data-checkout-section="insurance-details">
        <div class="flex flex-col gap-1 pl-6">
            <label class="text-sm font-medium" for="insurance-birthdate">{t('Geburtsdatum für Versicherungsschutz')}</label>
            <input
                id="insurance-birthdate"
                type="date"
                class="border border-gray-102 rounded-md px-3 py-2 text-sm w-48"
                bind:value={birthdate}
                data-form-field="insurance-birthdate"
            />
            {#if showErrors && birthdateErr}
                <span class="text-xs text-scarlet" data-form-error="insurance-birthdate">{birthdateErr}</span>
            {/if}
        </div>

        <Checkbox
            label={t('Ich bin einverstanden, meine persönlichen Daten mit der Bikmo GmbH zu teilen.')}
            name="insurance-consent"
            bind:value={consent}
            class="text-cyprus font-light text-sm"
            data-checkout-action="insurance-consent"
        />
        {#if showErrors && consentErr}
            <span class="text-xs text-scarlet" data-form-error="insurance-consent">{consentErr}</span>
        {/if}

        <Checkbox
            label={t('Ich akzeptiere die <a href="https://bikmo.com/at/documents/at-fahrrad-avb/" class="underline" target="_blank" rel="noopener">AVB</a>, bestätige, dass ich die <a href="https://support.bikmo.com/hc/en-us/articles/115005866249-Eignungskriterien" class="underline" target="_blank" rel="noopener">Eignungskriterien</a> erfülle, und habe das <a href="https://bikmo.com/at/documents/at-fahrrad-ipid/" class="underline" target="_blank" rel="noopener">Produktinformationsblatt</a> zur Versicherung sowie die Datenschutzerklärung gelesen und verstanden.')}
            name="insurance-terms"
            bind:value={terms}
            class="text-cyprus font-light text-sm"
            data-checkout-action="insurance-terms"
        />
        {#if showErrors && termsErr}
            <span class="text-xs text-scarlet" data-form-error="insurance-terms">{termsErr}</span>
        {/if}
    </div>
    {/if}
    {/if}
</div>
<svg width="45" height="52" viewBox="0 0 45 52" class="shrink-0" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.24219 8.66415V25.8141C1.24219 42.9641 22.3394 50.3141 22.3394 50.3141C22.3394 50.3141 43.4366 42.9641 43.4366 25.8141V8.66415L22.3394 1.31415L1.24219 8.66415Z" stroke="#002E40" stroke-width="2.48203" stroke-linecap="square"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M29.8478 14.1767L26.4153 15.2082L28.7013 21.1868C30.0789 20.8494 31.5333 21.0496 32.7685 21.7467C34.0037 22.4439 34.9267 23.5855 35.3498 24.9393C35.7728 26.2931 35.6641 27.7572 35.0457 29.0336C34.4273 30.31 33.3456 31.3027 32.021 31.8097C30.6964 32.3167 29.2284 32.2999 27.9158 31.7626C26.6031 31.2254 25.5446 30.2081 24.9556 28.9178C24.3667 27.6275 24.2915 26.1613 24.7455 24.8176C25.1995 23.4739 26.1486 22.3538 27.3994 21.6851L26.2508 18.6812L22.2906 26.9068C22.2335 27.025 22.1443 27.1246 22.033 27.1943C21.9218 27.2639 21.7932 27.3008 21.662 27.3006H18.8309C18.652 28.7066 17.9444 29.9917 16.8521 30.8947C15.7597 31.7977 14.3644 32.2509 12.9499 32.1622C11.5353 32.0734 10.2077 31.4494 9.23673 30.4169C8.26578 29.3844 7.72441 28.0209 7.72266 26.6036C7.7216 25.1846 8.26165 23.8185 9.23276 22.7838C10.2039 21.7491 11.533 21.1236 12.9492 21.0347C14.3655 20.9458 15.7624 21.4003 16.8552 22.3055C17.948 23.2108 18.6546 24.4986 18.8309 25.9067H20.5733L17.1721 16.3833C17.1453 16.3082 17.1317 16.2289 17.1317 16.1491H16.0862C15.9014 16.1491 15.7241 16.0757 15.5934 15.945C15.4627 15.8143 15.3893 15.637 15.3893 15.4522C15.3893 15.2673 15.4627 15.0901 15.5934 14.9593C15.7241 14.8286 15.9014 14.7552 16.0862 14.7552H19.2226C19.4074 14.7552 19.5847 14.8286 19.7154 14.9593C19.8461 15.0901 19.9196 15.2673 19.9196 15.4522C19.9196 15.637 19.8461 15.8143 19.7154 15.945C19.5847 16.0757 19.4074 16.1491 19.2226 16.1491H18.5688L19.8526 19.7434L25.5169 16.7625L24.8443 15.004C24.8101 14.9145 24.7947 14.8188 24.7991 14.7231C24.8035 14.6273 24.8276 14.5335 24.87 14.4475C24.9123 14.3614 24.9719 14.2851 25.0451 14.2231C25.1182 14.1612 25.2034 14.115 25.2952 14.0875L29.4471 12.842L29.8478 14.1767ZM20.3259 21.0697L21.7003 24.917L24.6485 18.7948L20.3259 21.0697ZM29.3746 26.8524L27.9019 23.0003C26.9988 23.5331 26.3289 24.3857 26.0248 25.3892C25.7207 26.3927 25.8046 27.4738 26.26 28.4183C26.7155 29.3627 27.5091 30.1017 28.4836 30.4886C29.4581 30.8756 30.5424 30.8823 31.5217 30.5075C32.501 30.1326 33.3037 29.4036 33.7708 28.4649C34.2379 27.5261 34.3353 26.4462 34.0436 25.439C33.752 24.4318 33.0927 23.5709 32.1963 23.027C31.2999 22.483 30.2319 22.2956 29.2038 22.502L30.6765 26.3548C30.7395 26.5268 30.7323 26.7167 30.6564 26.8834C30.5805 27.0501 30.442 27.1802 30.2709 27.2456C30.0998 27.311 29.9099 27.3064 29.7421 27.2328C29.5744 27.1592 29.4424 27.0226 29.3746 26.8524ZM17.4223 25.9067H13.2984C13.1135 25.9067 12.9363 25.9801 12.8056 26.1108C12.6748 26.2415 12.6014 26.4188 12.6014 26.6036C12.6014 26.7885 12.6748 26.9657 12.8056 27.0965C12.9363 27.2272 13.1135 27.3006 13.2984 27.3006H17.4223C17.2448 28.3321 16.687 29.2597 15.859 29.9C15.031 30.5404 13.993 30.8471 12.95 30.7596C11.9069 30.672 10.9346 30.1967 10.2249 29.4273C9.51519 28.6578 9.11974 27.6503 9.11659 26.6036C9.11563 25.5544 9.50911 24.5432 10.219 23.7706C10.9288 22.9979 11.9032 22.5204 12.9487 22.4327C13.9942 22.3449 15.0345 22.6534 15.8632 23.2969C16.6919 23.9405 17.2484 24.872 17.4223 25.9067Z" fill="#002E40"/>
</svg>
</div>
{/if}
